import React, { createContext, useState, useEffect } from 'react'
import { GeneralSnackbar } from '../../Components/Snackbar/generalSnackbar'
import { FirebaseErrorSnackbar } from '../../Components/Snackbar/FirebaseErrorSnackbar'

export type SnackbarData = {
  text: string
  color: 'red' | 'blue' | 'green'
}

type SnackbarContextType = {
  toggleShowSnackbar: (snackbarData: SnackbarData) => void
  showFirebaseErrorSnackbar: (onRefresh: () => void) => void
}

export const SnackbarContext = createContext<SnackbarContextType | null>(null)

export const SnackbarProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false)
  const [snackbarData, setSnackbarData] = useState<SnackbarData | null>(null)
  const [showFirebaseError, setShowFirebaseError] = useState<boolean>(false)
  const [refreshCallback, setRefreshCallback] = useState<(() => void) | null>(null)

  const toggleShowSnackbar = (snackbarData: SnackbarData) => {
    setSnackbarData(snackbarData)
    setShowSnackbar(true)
  }

  const showFirebaseErrorSnackbar = (onRefresh: () => void) => {
    setRefreshCallback(() => onRefresh)
    setShowFirebaseError(true)
    setShowSnackbar(false)
  }

  const handleSnackbarClose = () => {
    setShowSnackbar(false)
    setSnackbarData(null)
  }

  const handleFirebaseErrorClose = () => {
    setShowFirebaseError(false)
    setRefreshCallback(null)
  }

  const handleRefresh = () => {
    if (refreshCallback) {
      refreshCallback()
    }
  }

  useEffect(() => {
    if (!showSnackbar) return

    const timer = setTimeout(() => {
      handleSnackbarClose()
    }, 5000)

    return () => {
      clearTimeout(timer)
    }
  }, [showSnackbar, snackbarData])

  return (
    <SnackbarContext.Provider
      value={{
        toggleShowSnackbar,
        showFirebaseErrorSnackbar,
      }}
    >
      {children}
      {showSnackbar && snackbarData && (
        <GeneralSnackbar
          text={snackbarData.text}
          color={snackbarData.color}
          show={showSnackbar}
          onClose={handleSnackbarClose}
        />
      )}
      {showFirebaseError && (
        <FirebaseErrorSnackbar
          show={showFirebaseError}
          onClose={handleFirebaseErrorClose}
          onRefresh={handleRefresh}
        />
      )}
    </SnackbarContext.Provider>
  )
}
